import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import QRCode from 'qrcode'
import { vocabulary } from './vocabulary'

/** The nouns a person meets here — copy is centralized in `vocabulary.ts` so a vertical overrides it
 *  at build time. Re-exported for the `Info` term type and any external consumers. */
export const GLOSSARY = vocabulary.glossary

/** Shared generic words (Cancel, Copy, Working…) — the vocabulary's `common` section. */
const c = vocabulary.common

/** A hoverable, tappable, keyboard-reachable explanation. Positioned by the browser's title-less
 *  popover pattern: a small button that reveals a card, closing on Escape or outside click. */
export function Info({ term }: { term: keyof typeof GLOSSARY }) {
  const [open, setOpen] = useState(false)
  const [popStyle, setPopStyle] = useState<CSSProperties>({})
  const box = useRef<HTMLSpanElement>(null)
  const entry = GLOSSARY[term]!

  useEffect(() => {
    if (!open) return
    const close = (event: Event) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  // Keep the card on screen. On a phone it is wider than any inline anchor can safely place, so it
  // spans the viewport with a margin; on a wider screen it anchors to whichever side of the dot
  // leaves room.
  function reveal() {
    const rect = box.current?.getBoundingClientRect()
    if (rect) {
      if (window.innerWidth < 560) {
        setPopStyle({ position: 'fixed', left: 16, right: 16, top: rect.bottom + 6, width: 'auto', maxWidth: 'none' })
      } else {
        const right = rect.left > window.innerWidth * 0.5
        setPopStyle({ position: 'absolute', top: 24, ...(right ? { right: -8 } : { left: -8 }) })
      }
    }
    setOpen(true)
  }

  return (
    <span ref={box} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        className="info"
        aria-label={`${c.whatIsAPrefix}${entry.title.toLowerCase()}${c.whatIsASuffix}`}
        aria-expanded={open}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (open) setOpen(false); else reveal() }}
        onMouseEnter={reveal}
        onMouseLeave={() => setOpen(false)}
      >
        i
      </button>
      {open && (
        <span className="pop" style={popStyle} role="tooltip">
          <b>{entry.title}</b>
          {entry.body}
        </span>
      )}
    </span>
  )
}

export function Chip({ kind, children }: { kind?: 'ok' | 'wait' | 'stop'; children: ReactNode }) {
  return (
    <span className={kind ? `chip ${kind}` : 'chip'}>
      {kind && <span className="dot" aria-hidden="true" />}
      {children}
    </span>
  )
}

export function Card({ title, action, footer, children }: {
  title?: ReactNode
  action?: ReactNode
  footer?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="card">
      {(title || action) && (
        <header className="card-hd">
          <b>{title}</b>
          {action}
        </header>
      )}
      <div className="card-bd">{children}</div>
      {footer && <footer className="card-ft">{footer}</footer>}
    </section>
  )
}

export function Empty({ line, action }: { line: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <p className="small">{line}</p>
      {action}
    </div>
  )
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="stack" style={{ gap: 10, padding: 4 }} aria-busy="true" aria-label={c.loading}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 17, width: `${92 - i * 13}%` }} />
      ))}
    </div>
  )
}

export function Modal({ title, onClose, children, footer }: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  const headingId = useId()

  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [onClose])

  return (
    <div className="scrim" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={headingId}>
        <header className="modal-hd">
          <h2 id={headingId}>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={c.close}>✕</button>
        </header>
        <div className="modal-bd">{children}</div>
        {footer && <div className="modal-ft">{footer}</div>}
      </div>
    </div>
  )
}

/** A destructive-or-confirming decision, in the same dialog the rest of the app uses — so a
 *  "remove this person?" never falls back to the browser's grey confirm box. */
export function Confirm({ title, body, confirmLabel = c.confirm, danger, busy, onConfirm, onClose }: {
  title: string
  body: ReactNode
  confirmLabel?: string
  danger?: boolean
  busy?: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack" style={{ gap: 20 }}>
        <div className="muted" style={{ fontSize: 'var(--t-2)', lineHeight: 1.5 }}>{body}</div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>{c.cancel}</button>
          <button
            type="button"
            className={danger ? 'btn danger' : 'btn primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? c.working : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/** A live mm:ss countdown to an expiry, re-rendering each second. A short-lived code a person is
 *  watching needs to visibly tick down — a static "in 5 minutes" never moves and reads as stuck. */
export function Countdown({ to, prefix = c.countdownPrefix, expired = c.countdownExpired }: {
  to: string
  prefix?: string
  expired?: string
}) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])
  const remaining = Math.max(0, Math.round((new Date(to).getTime() - now) / 1000))
  if (remaining <= 0) return <>{expired}</>
  const minutes = Math.floor(remaining / 60)
  const seconds = remaining % 60
  return <span className="nums">{prefix} {minutes}:{String(seconds).padStart(2, '0')}</span>
}

/** Previous / next paging for a list, hidden when there is only one page. */
export function Pager({ page, pageCount, onPage }: {
  page: number
  pageCount: number
  onPage: (page: number) => void
}) {
  if (pageCount <= 1) return null
  return (
    <div className="pager">
      <span className="tiny faint nums">{c.pageWord} {page + 1} {c.ofWord} {pageCount}</span>
      <div className="pages">
        <button className="btn small" disabled={page <= 0} onClick={() => onPage(page - 1)}>
          {c.previous}
        </button>
        <button className="btn small" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)}>
          {c.next}
        </button>
      </div>
    </div>
  )
}

/** A QR the person can scan, download as a PNG, or send as a link. Rendered locally — the code
 *  never travels to anyone's image service. */
export function Qr({ payload, download, actions }: { payload: string; download: string; actions?: ReactNode }) {
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    QRCode.toDataURL(payload, { errorCorrectionLevel: 'H', margin: 1, width: 420 })
      .then((url) => live && setSrc(url))
      .catch(() => live && setSrc(null))
    return () => { live = false }
  }, [payload])

  if (!src) return <div className="skeleton" style={{ width: 'min(420px, 36vh)', maxWidth: '100%', aspectRatio: '1 / 1', borderRadius: 7 }} />

  return (
    <div className="stack" style={{ gap: 10, alignItems: 'center' }}>
      <span className="qr-frame">
        <img src={src} alt={c.qrAlt} />
      </span>
      <div className="row wrap-row" style={{ justifyContent: 'center' }}>
        <a className="btn small" href={src} download={`${download}.png`}>{c.downloadQr}</a>
        {actions}
      </div>
    </div>
  )
}

/** Time a person can act on: "in 4 min", "in 23 hours" — never a timestamp they must subtract. */
export function expiresIn(iso: string): string {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000)
  if (seconds <= 0) return c.timeExpired
  if (seconds < 90) return `${c.inPrefix} ${seconds} ${c.seconds}`
  if (seconds < 3600) return `${c.inPrefix} ${Math.round(seconds / 60)} ${c.minutes}`
  if (seconds < 172800) return `${c.inPrefix} ${Math.round(seconds / 3600)} ${c.hours}`
  return `${c.inPrefix} ${Math.round(seconds / 86400)} ${c.days}`
}

/** A date a person reads at a glance: "24 Jul", or "24 Jul 2025" once the year matters. */
export function shortDate(iso?: string): string {
  if (!iso) return '—'
  const date = new Date(iso)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

/** Copy-to-clipboard that reports back, because a button that silently succeeds is a button
 *  people press twice. */
export function CopyButton({ value, label = c.copyLink }: { value: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      className="btn small"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setDone(true)
          setTimeout(() => setDone(false), 1800)
        })
      }}
    >
      {done ? c.copied : label}
    </button>
  )
}
